/**
 * Generic helpers for FHIR item trees (Questionnaire.item, QuestionnaireResponse.item). Any item type with linkId and
 * optional nested item[] can use these.
 */

/** Item that has linkId and optional nested items (Questionnaire item or QuestionnaireResponse item). */
export interface FhirItemWithLinkId<T = unknown> {
  linkId: string;
  item?: T[];
}

export function findItemByLinkId<T extends FhirItemWithLinkId<T>>(items: T[], linkId: string): T | undefined {
  for (const item of items) {
    if (item.linkId === linkId) return item;
    if (item.item?.length) {
      const found = findItemByLinkId(item.item as T[], linkId);
      if (found) return found;
    }
  }
  return undefined;
}

export function replaceItemByLinkId<T extends FhirItemWithLinkId<T>>(items: T[], linkId: string, replacement: T): T[] {
  return items.map((it) => {
    if (it.linkId === linkId) return replacement;
    if (it.item?.length) {
      return {
        ...it,
        item: replaceItemByLinkId(it.item as T[], linkId, replacement) as T['item'],
      } as T;
    }
    return it;
  });
}

export function patchItemByLinkId<T extends FhirItemWithLinkId<T>>(
  items: T[],
  linkId: string,
  partial: Partial<T>
): T[] {
  return items.map((it) => {
    if (it.linkId === linkId) {
      return { ...it, ...partial } as T;
    }
    if (it.item?.length) {
      return {
        ...it,
        item: patchItemByLinkId(it.item as T[], linkId, partial) as T['item'],
      } as T;
    }
    return it;
  });
}

/** Merge two item arrays by linkId: replace existing, append new. Preserves order of existing. */
export function mergeItemsByLinkId<T extends FhirItemWithLinkId<T>>(existing: T[], updates: T[]): T[] {
  const updateMap = new Map(updates.map((i) => [i.linkId, i]));
  const result: T[] = [];
  const seen = new Set<string>();
  for (const i of existing) {
    const u = updateMap.get(i.linkId);
    result.push((u ?? i) as T);
    seen.add(i.linkId);
  }
  for (const u of updates) {
    if (!seen.has(u.linkId)) {
      result.push(u);
      seen.add(u.linkId);
    }
  }
  return result;
}
